import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { connectField } from 'uniforms';
import { publicFetch } from '../data/services/clientLoaders';

type Doc = { id: number; title: string; type?: string };

// tenant/company are in the (proxied) editor URL, e.g.
// /admin/tenants/33/companies/37/screens/create/edit/3449/screen
const parseTenantCompany = (pathname?: string | null) => {
  const m = pathname?.match(/tenants\/(\d+)\/companies\/(\d+)/);
  return m ? { tenantId: m[1], companyId: m[2] } : null;
};

const looksLikeUrl = (q: string) => /^(https?:\/\/|\/)/.test(q.trim());

/**
 * Uniforms field for a link's href.
 *
 * The text box IS the href field (same reliable binding the plain URL input
 * used, so "Ok" always registers on the first press). It doubles as a search
 * box: typing filters the document list below, and clicking a document fills
 * the box with `/documents/{id}`. A pasted URL is kept verbatim.
 */
export const DocumentSelectField = connectField(
  ({ value, onChange, label }: any) => {
    const pathname = usePathname();
    const [documents, setDocuments] = useState<Doc[]>([]);

    useEffect(() => {
      const ids = parseTenantCompany(pathname);
      if (!ids) return;
      let active = true;
      (async () => {
        try {
          const docs: Doc[] = await publicFetch(
            `${process.env.NEXT_PUBLIC_API_URL}/tenants/${ids.tenantId}/companies/${ids.companyId}/documents/all`,
            {}
          );
          if (active && Array.isArray(docs)) setDocuments(docs);
        } catch {
          // Fetch failed — a URL can still be typed straight into the box.
        }
      })();
      return () => {
        active = false;
      };
    }, [pathname]);

    const href: string = value ?? '';
    const isUrl = looksLikeUrl(href);

    // Store a portable, context-neutral href. Screen content is authored here in
    // the admin editor but viewed by end users in the consumer portal, which
    // resolves `/documents/{id}` to the correct portal route via middleware.
    // Baking in an `/admin/...` path broke those links in the consumer, so keep
    // the bare form; the admin editor resolves it to its own document route on
    // click (see pages/edit.tsx).
    const buildDocHref = (id: number) => `/documents/${id}`;

    // Which document (if any) the current value points at. Matches a trailing
    // `/documents/{id}` so it works for both the full path and legacy bare paths.
    const selectedId = useMemo(() => {
      const m = /\/documents\/(\d+)$/.exec(href);
      return m ? Number(m[1]) : null;
    }, [href]);
    const selectedDoc = useMemo(
      () => documents.find((d) => d.id === selectedId) ?? null,
      [documents, selectedId]
    );

    // When the box holds a chosen link (/… or http…) don't filter by it; show
    // the whole list. Otherwise treat the text as a title search.
    const filtered = useMemo(() => {
      const q = href.trim().toLowerCase();
      const list =
        !q || isUrl
          ? documents
          : documents.filter((d) => d.title.toLowerCase().includes(q));
      return list.slice(0, 50);
    }, [documents, href, isUrl]);

    return (
      <div style={{ minWidth: 360 }}>
        <TextField
          fullWidth
          variant="outlined"
          label={label ?? 'Document'}
          placeholder="Search a document, or paste a URL"
          value={href}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          helperText={
            selectedDoc ? `Selected document: ${selectedDoc.title}` : undefined
          }
        />

        <List
          dense
          style={{
            maxHeight: 260,
            overflow: 'auto',
            marginTop: 6,
            border: '1px solid #e0e0e0',
            borderRadius: 4,
          }}
        >
          {filtered.map((d) => (
            <ListItemButton
              key={d.id}
              selected={d.id === selectedId}
              onClick={() => onChange(buildDocHref(d.id))}
            >
              <ListItemText
                primary={`${d.title}${d.type === 'SCREEN' ? '  (screen)' : ''}`}
              />
            </ListItemButton>
          ))}

          {filtered.length === 0 ? (
            <div style={{ padding: 12, color: '#999', fontSize: 13 }}>
              {documents.length === 0 ? 'Loading documents…' : 'No matches'}
            </div>
          ) : null}
        </List>
      </div>
    );
  }
);

export default DocumentSelectField;
